// src/components/google_ads/GoogleAdsOAuthCallback.tsx
// Frontend-hosted OAuth landing. Google redirects here after consent.
// Reads ?code=...&state=... from the query string, calls
// /oauth/exchange, and bounces back to the Google Ads overview.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

import { ApiError, exchangeOAuth } from '@/lib/api_google_ads';

export function GoogleAdsOAuthCallback() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<'running' | 'ok' | 'err'>('running');
  const [error, setError] = useState('');

  useEffect(() => {
    const code = search.get('code') || '';
    const oauthState = search.get('state') || '';
    const googleError = search.get('error') || '';
    if (googleError) {
      setState('err');
      setError(`Google devolvió: ${googleError}`);
      return;
    }
    if (!code || !oauthState) {
      setState('err');
      setError('Faltan parámetros code o state en la URL.');
      return;
    }
    (async () => {
      try {
        await exchangeOAuth(code, oauthState);
        setState('ok');
        // Short delay so the user sees the success state before we nav.
        setTimeout(() => navigate('/m/mkt-google/dashboard'), 1200);
      } catch (e: any) {
        setState('err');
        setError(e instanceof ApiError ? e.message : e?.message || 'Error');
      }
    })();
  }, [search, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 max-w-md w-full text-center">
        <h2 className="text-xl font-semibold text-white mb-4">Google Ads — OAuth</h2>
        {state === 'running' && (
          <div className="flex flex-col items-center gap-3 text-gray-300">
            <Loader2 className="animate-spin text-emerald-400" size={28} />
            Intercambiando código…
          </div>
        )}
        {state === 'ok' && (
          <div className="flex flex-col items-center gap-3 text-emerald-400">
            <CheckCircle size={32} />
            Conectado. Redirigiendo…
          </div>
        )}
        {state === 'err' && (
          <div className="space-y-3">
            <div className="flex flex-col items-center gap-2 text-red-400">
              <XCircle size={28} />
              <div className="text-sm">{error}</div>
            </div>
            <Link
              to="/m/mkt-google/dashboard"
              className="o-btn o-btn-ghost inline-flex items-center gap-2"
            >
              Volver
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default GoogleAdsOAuthCallback;
